import { escapeHtml as esc } from './escapeHtml.js';
/**
 * Shared per-slot equip picker.
 *
 * The Sparring Hall, Profile, and Inventory pages all show the player's
 * loadout. Rather than make players hunt through their bag to swap gear, each
 * slot renders a dropdown populated with the eligible items they already own.
 *
 * We use a CUSTOM picker (button + popup list) rather than a native <select>,
 * because native <option> elements can only render plain text — no colour, no
 * line breaks. To show each item's name with its combat stats stacked beneath
 * in green, we need real DOM we can style. This module owns the markup, CSS,
 * the equip/unequip call, and the open/close wiring so all three pages behave
 * identically.
 */

import { store } from '../state/store.js';
import { api, HttpError } from '../api/client.js';
import { ITEM_TIER_LABELS } from '@rex-mortis/shared';
import type { InventoryItemDto, ItemCategory, LoadoutSlotDto } from '@rex-mortis/shared';

export type SlotKey =
  | 'primary' | 'secondary' | 'helm' | 'chest' | 'amulet'
  | 'rod' | 'spear' | 'pickaxe' | 'axe' | 'basket';

/** Gathering tool slots → the slug prefix their tools use. */
const TOOL_SLOT_PREFIX: Partial<Record<SlotKey, string>> = {
  rod: 'rod_',
  spear: 'spear_',
  pickaxe: 'pickaxe_',
  axe: 'axe_',
  basket: 'basket_',
};

/** A dagger/dirk — the only weapon light enough for the off-hand. Matches the
 *  server's isOffHandWeapon slug convention. */
function isOffHandSlug(slug: string): boolean {
  return slug.startsWith('dagger_') || slug.startsWith('dirk_');
}

/** Whether an item may be equipped in the given slot. Mirrors the server's
 *  slotAcceptsItem exactly so the picker only offers valid choices: the
 *  off-hand takes a shield or a dagger, never a two-handed/main weapon. */
export function slotAccepts(slotKey: SlotKey, it: { category: ItemCategory; slug: string }): boolean {
  switch (slotKey) {
    case 'primary':
      return it.category === 'melee' || it.category === 'ranged';
    case 'secondary':
      return (
        it.category === 'armor_shield' ||
        ((it.category === 'melee' || it.category === 'ranged') && isOffHandSlug(it.slug))
      );
    case 'helm':
      return it.category === 'armor_head';
    case 'chest':
      return it.category === 'armor_chest';
    case 'amulet':
      return it.category === 'amulet';
    case 'rod':
    case 'spear':
    case 'pickaxe':
    case 'axe':
    case 'basket': {
      // Gathering tool slots — match the tool by its slug prefix.
      const prefix = TOOL_SLOT_PREFIX[slotKey];
      return it.category === 'tool' && !!prefix && it.slug.startsWith(prefix);
    }
  }
}

/** Drop-in CSS for the slot picker. Each page appends this to its style block
 *  so the control looks the same everywhere. */
export const EQUIP_PICKER_CSS = `
  .slot-picker { position: relative; margin-top: 4px; }
  .slot-picker-btn {
    width: 100%;
    display: flex; align-items: center; justify-content: space-between; gap: 8px;
    padding: 8px 10px;
    background: var(--c-parchment);
    border: 1px solid var(--c-rule);
    border-radius: var(--radius);
    color: var(--c-ink);
    font: inherit;
    text-align: left;
    cursor: pointer;
    min-height: 40px;
  }
  .slot-picker-btn:hover { border-color: var(--c-gold); }
  .slot-picker-btn[aria-expanded="true"] {
    border-color: var(--c-gold);
    box-shadow: 0 0 0 2px rgba(212, 169, 60, 0.25);
  }
  .slot-caret { color: var(--c-ink-soft); flex: 0 0 auto; font-size: 0.8rem; }
  .slot-picker-label { min-width: 0; display: flex; flex-direction: column; gap: 2px; }
  .sp-name { color: var(--c-ink); overflow-wrap: anywhere; line-height: 1.25; }
  .sp-empty { font-style: italic; color: var(--c-ink-soft); }
  .sp-stats { display: flex; flex-direction: column; gap: 0; }
  .sp-stat { color: var(--c-success); font-family: var(--font-numeric); font-size: 0.8rem; line-height: 1.3; }
  .slot-menu {
    position: absolute; top: calc(100% + 4px); left: 0; right: 0;
    z-index: 60;
    background: var(--c-parchment);
    border: 1px solid var(--c-rule);
    border-radius: var(--radius);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
    max-height: 300px; overflow-y: auto;
    padding: 4px;
  }
  .slot-menu[hidden] { display: none; }
  .slot-opt {
    display: flex; flex-direction: column; gap: 2px;
    width: 100%; text-align: left;
    padding: 8px 10px;
    background: transparent; border: 0;
    border-radius: calc(var(--radius) - 2px);
    color: var(--c-ink);
    font: inherit;
    cursor: pointer;
  }
  .slot-opt:hover { background: rgba(139, 111, 71, 0.14); }
  .slot-opt.is-current { background: rgba(212, 169, 60, 0.18); }
  .slot-opt[disabled] { opacity: 0.5; cursor: default; }
  /* Tier label under the item name — same palette as the inventory grid badge
     (low tiers tinted text, legendary/artifact gold-fill, mythic amethyst) so it
     reads in both themes and passes the contrast guard. */
  .sp-tier {
    align-self: flex-start;
    font-family: var(--font-display);
    font-size: 0.7rem;
    letter-spacing: 0.03em;
    padding: 0 6px;
    border-radius: 3px;
    border: 1px solid var(--c-rule-soft);
    background: var(--c-parchment-dark);
    margin: 1px 0;
  }
  .sp-tier.crude       { color: var(--c-ink-soft); }
  .sp-tier.common      { color: var(--c-ink); }
  .sp-tier.fine        { color: var(--c-success); }
  .sp-tier.masterwork  { color: var(--c-oxblood); }
  /* Legendary and above escalate into premium "seals" so rarity reads at a
     glance: legendary = embossed burnished gold, artifact = radiant glowing
     gold with a light-glint sweep, mythic = glowing amethyst gem with a
     brighter, faster sweep. */
  .sp-tier.legendary,
  .sp-tier.artifact,
  .sp-tier.mythic {
    position: relative;
    overflow: hidden;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.045em;
  }
  .sp-tier.legendary {
    background: linear-gradient(155deg, var(--c-gold-pale) 0%, var(--c-gold-bright) 45%, var(--c-gold) 100%);
    color: var(--c-on-gold);
    border-color: var(--c-rule-strong);
    box-shadow: inset 0 1px 0 rgba(255, 250, 225, 0.6), inset 0 -2px 3px rgba(120, 80, 15, 0.3), 0 1px 2px rgba(0, 0, 0, 0.25);
    text-shadow: 0 1px 0 rgba(255, 248, 225, 0.55);
  }
  .sp-tier.artifact {
    background: linear-gradient(155deg, #fff4c9 0%, #ffce5a 38%, #f0a83a 70%, #c9801f 100%);
    color: var(--c-on-gold);
    border-color: #9a5e16;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7), 0 0 9px rgba(245, 165, 55, 0.65), 0 1px 2px rgba(0, 0, 0, 0.25);
    text-shadow: 0 1px 0 rgba(255, 245, 215, 0.5);
  }
  .sp-tier.mythic {
    background: linear-gradient(155deg, #f4e6ff 0%, #c89dff 32%, #9156e9 68%, #5e2bb8 100%);
    color: #fff;
    border-color: #4a1f93;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.5), 0 0 11px rgba(165, 95, 255, 0.75), 0 1px 2px rgba(0, 0, 0, 0.3);
    text-shadow: 0 1px 2px rgba(40, 0, 80, 0.55);
  }
  .sp-tier.artifact::after,
  .sp-tier.mythic::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(115deg, transparent 35%, rgba(255, 255, 255, 0.55) 50%, transparent 65%);
    transform: translateX(-130%);
    animation: tier-shimmer 3.4s ease-in-out infinite;
    pointer-events: none;
  }
  .sp-tier.mythic::after {
    animation-duration: 2.7s;
    background: linear-gradient(115deg, transparent 35%, rgba(255, 255, 255, 0.72) 50%, transparent 65%);
  }
  @media (prefers-reduced-motion: reduce) {
    .sp-tier.artifact::after,
    .sp-tier.mythic::after { animation: none; opacity: 0; }
  }
`;

/** Green, stacked stat lines for an item: "+32 atk" / "+9 acc" / "+50 def". */
function statLinesHtml(it: InventoryItemDto): string {
  const lines: string[] = [];
  if (it.attack) lines.push(`<span class="sp-stat">+${it.attack} atk</span>`);
  if (it.accuracy) lines.push(`<span class="sp-stat">+${it.accuracy} acc</span>`);
  if (it.defense) lines.push(`<span class="sp-stat">+${it.defense} def</span>`);
  return lines.length ? `<span class="sp-stats">${lines.join('')}</span>` : '';
}

/** Inner markup for one item row / the current-selection label.
 *  Name, then the tier label in its tier colour, then the green stat lines.
 *  No stack count: in an equip picker you equip one piece, so the quantity owned
 *  (only ever >1 for stacking tools) is just noise here. */
function itemInnerHtml(it: InventoryItemDto): string {
  return `<span class="sp-name">${esc(it.name)}</span><span class="sp-tier ${it.tier}">${ITEM_TIER_LABELS[it.tier]}</span>${statLinesHtml(it)}`;
}

/**
 * A custom equip dropdown for one slot: a button showing the current item
 * (name + green stat lines) and a popup list of every eligible owned item,
 * each with the same name + stat layout, plus "— none —" to unequip. The
 * caller wraps this in its own card + heading.
 */
/** Score an item by the slot's PRIMARY combat stat so the picker shows the
 *  best piece at the top. Primary/secondary weapons rank by attack (then
 *  accuracy); shields and armour rank by defense. Ties break on item value
 *  so higher-tier gear still wins when the headline stat is identical. */
function slotScore(slotKey: SlotKey, it: InventoryItemDto): number {
  const atk = it.attack ?? 0;
  const acc = it.accuracy ?? 0;
  const def = it.defense ?? 0;
  if (slotKey === 'primary' || slotKey === 'secondary') {
    // Off-hand daggers in `secondary` still rank by attack; shields by defense.
    // We can tell which by whether the item has an attack stat at all.
    if (atk > 0) return atk * 1000 + acc;
    return def * 1000;
  }
  // helm / chest
  return def * 1000;
}

export function slotPickerHtml(
  slotKey: SlotKey,
  slot: LoadoutSlotDto,
  inventory: InventoryItemDto[],
): string {
  // Strongest piece at the top so the player doesn't have to scan the whole
  // list to find their best option for the slot.
  const eligible = inventory
    .filter((it) => slotAccepts(slotKey, it))
    .sort((a, b) => slotScore(slotKey, b) - slotScore(slotKey, a));
  const current = eligible.find((it) => it.slug === slot.itemSlug) ?? null;

  const label = current
    ? itemInnerHtml(current)
    : slot.itemName
      ? `<span class="sp-name">${esc(slot.itemName)}</span>`
      : `<span class="sp-name sp-empty">${eligible.length === 0 ? 'none owned' : '— nothing equipped —'}</span>`;

  const noneOpt = `<button type="button" class="slot-opt${slot.itemSlug ? '' : ' is-current'}" data-value="" role="option"><span class="sp-name sp-empty">— none —</span></button>`;
  const itemOpts = eligible
    .map(
      (it) =>
        `<button type="button" class="slot-opt${it.slug === slot.itemSlug ? ' is-current' : ''}" data-value="${esc(it.slug)}" role="option">${itemInnerHtml(it)}</button>`,
    )
    .join('');

  return `
    <div class="slot-picker" data-slot="${slotKey}">
      <button type="button" class="slot-picker-btn" aria-haspopup="listbox" aria-expanded="false">
        <span class="slot-picker-label">${label}</span>
        <span class="slot-caret" aria-hidden="true">▾</span>
      </button>
      <div class="slot-menu" role="listbox" hidden>${noneOpt}${itemOpts}</div>
    </div>`;
}

export interface SlotChangeResult {
  kind: 'success' | 'danger';
  message: string;
}

/** Equip the given item (or unequip when slug is empty) and update the store. */
export async function performSlotEquip(slot: SlotKey, slug: string): Promise<SlotChangeResult> {
  try {
    if (slug) {
      const r = await api.equip({ slot, itemSlug: slug });
      store.set({ character: r.character });
      return { kind: 'success', message: `Equipped ${r.itemName}.` };
    }
    const r = await api.unequip({ slot });
    store.set({ character: r.character });
    return { kind: 'success', message: `Unequipped ${slot}.` };
  } catch (err) {
    return {
      kind: 'danger',
      message: err instanceof HttpError ? err.message : 'Could not change equipment.',
    };
  }
}

// Bind the click-outside-to-close handler exactly once for the app lifetime.
// It works across re-renders because it queries the live DOM each click.
let outsideBound = false;
function bindOutsideClose(): void {
  if (outsideBound) return;
  outsideBound = true;
  document.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    if (t.closest('.slot-picker')) return;
    document.querySelectorAll<HTMLElement>('.slot-menu:not([hidden])').forEach((m) => {
      m.hidden = true;
    });
    document
      .querySelectorAll<HTMLButtonElement>('.slot-picker-btn[aria-expanded="true"]')
      .forEach((b) => b.setAttribute('aria-expanded', 'false'));
  });
}

/**
 * Wire every `.slot-picker` within `root`: toggle its menu open/closed, close
 * sibling menus, and on choosing an option perform the equip/unequip then call
 * `onDone` with the result so the page can refresh + surface the message.
 */
export function wireSlotPickers(
  root: HTMLElement,
  onDone: (result: SlotChangeResult) => void | Promise<void>,
): void {
  bindOutsideClose();
  root.querySelectorAll<HTMLElement>('.slot-picker').forEach((picker) => {
    const btn = picker.querySelector<HTMLButtonElement>('.slot-picker-btn');
    const menu = picker.querySelector<HTMLElement>('.slot-menu');
    if (!btn || !menu) return;

    const closeAll = (): void => {
      root.querySelectorAll<HTMLElement>('.slot-menu').forEach((m) => {
        m.hidden = true;
      });
      root
        .querySelectorAll<HTMLButtonElement>('.slot-picker-btn')
        .forEach((b) => b.setAttribute('aria-expanded', 'false'));
    };

    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const isOpen = !menu.hidden;
      closeAll();
      if (!isOpen) {
        menu.hidden = false;
        btn.setAttribute('aria-expanded', 'true');
      }
    });

    menu.querySelectorAll<HTMLButtonElement>('.slot-opt').forEach((opt) => {
      opt.addEventListener('click', async (e) => {
        e.stopPropagation();
        const slotKey = picker.dataset.slot as SlotKey;
        const slug = opt.dataset.value ?? '';
        menu.hidden = true;
        btn.setAttribute('aria-expanded', 'false');
        menu.querySelectorAll<HTMLButtonElement>('.slot-opt').forEach((b) => (b.disabled = true));
        const result = await performSlotEquip(slotKey, slug);
        // Re-enable the options once the attempt resolves. On SUCCESS the caller
        // re-renders this picker away, so this is a harmless no-op on detached
        // nodes; on FAILURE (e.g. "you only own 1 — equip another copy") the
        // picker stays, and without this every option was left muted + unclickable.
        menu.querySelectorAll<HTMLButtonElement>('.slot-opt').forEach((b) => (b.disabled = false));
        await onDone(result);
      });
    });
  });
}
